import React, { useState, useRef } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

// StepListEditor — edit an ordered list of pipeline steps with a drag handle.
// Each "step" is an arbitrary JSON object; we treat the array as opaque and
// let users tweak individual step JSON in a small inline editor. Reordering
// is done via HTML5 drag-and-drop (no library dependency).
//
// props:
//   value: array — the current steps
//   onChange: (nextArray) => void
export default function StepListEditor({ value = [], onChange }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  // Track raw JSON per step so users can type invalid JSON mid-edit without
  // wiping their state on every keystroke.
  const jsonBufRef = useRef({});

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = value.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
    jsonBufRef.current = {}; // buffer keyed by index — reset after reorder
  };

  const addStep = () => {
    // A sensible default: named step with an empty config the user fills in.
    onChange([...value, { name: `step-${value.length + 1}`, config: {} }]);
  };

  const removeStep = (i) => {
    onChange(value.filter((_, j) => j !== i));
    delete jsonBufRef.current[i];
  };

  const patchStep = (i, next) => {
    const arr = value.slice();
    arr[i] = next;
    onChange(arr);
  };

  return (
    <div data-testid="step-list-editor" className="space-y-2">
      {value.length === 0 && (
        <div className="text-xs text-zinc-500 border border-dashed border-zinc-300 rounded-md p-4 text-center">
          No steps yet — click <strong>+ add step</strong> below to start.
        </div>
      )}

      {value.map((step, i) => {
        const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
        const rawKey = String(i);
        const rawJson =
          jsonBufRef.current[rawKey] ??
          JSON.stringify(step, null, 2);
        return (
          <div
            key={i}
            data-testid={`step-row-${i}`}
            draggable
            onDragStart={(e) => {
              setDragIdx(i);
              // Firefox needs this to actually kick off the drag.
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", String(i));
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragIdx !== null) setOverIdx(i);
            }}
            onDragLeave={() => {
              if (overIdx === i) setOverIdx(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              move(dragIdx, i);
              setDragIdx(null);
              setOverIdx(null);
            }}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            className={`border rounded-md bg-white transition ${
              isOver ? "border-[#3730A3] ring-2 ring-[#3730A3]/20" : "border-zinc-200"
            } ${dragIdx === i ? "opacity-50" : "opacity-100"}`}
          >
            <div className="flex items-start gap-2 p-2">
              {/* Drag handle */}
              <div
                data-testid={`step-drag-${i}`}
                className="cursor-move mt-1 text-zinc-400 hover:text-zinc-700 select-none"
                title="Drag to reorder"
              >
                <GripVertical className="w-4 h-4" />
              </div>

              <div className="flex-1 min-w-0">
                {/* Header row — step number + name preview */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    step {i + 1}
                  </span>
                  <span className="font-medium truncate">
                    {step?.name || step?.parser || "(unnamed)"}
                  </span>
                </div>

                {/* Inline JSON editor for this step */}
                <textarea
                  data-testid={`step-json-${i}`}
                  spellCheck={false}
                  value={rawJson}
                  onChange={(e) => {
                    jsonBufRef.current[rawKey] = e.target.value;
                    try {
                      const parsed = JSON.parse(e.target.value);
                      patchStep(i, parsed);
                    } catch {
                      // Invalid JSON — user is mid-edit, don't stomp state.
                    }
                  }}
                  onBlur={() => {
                    // Final validation on blur — warn only, don't revert.
                    try {
                      JSON.parse(jsonBufRef.current[rawKey] ?? rawJson);
                      delete jsonBufRef.current[rawKey]; // buffer cleared, use parsed value
                    } catch {
                      toast.error(`Step ${i + 1}: invalid JSON, changes not saved`);
                    }
                  }}
                  className="mt-1.5 w-full font-mono text-[11px] border border-zinc-200 rounded p-2 outline-none focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3]"
                  rows={Math.min(8, Math.max(3, rawJson.split("\n").length))}
                />
              </div>

              <button
                type="button"
                data-testid={`step-delete-${i}`}
                onClick={() => removeStep(i)}
                className="p-1.5 rounded hover:bg-red-50 text-red-500 flex-shrink-0"
                title="Remove step"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        data-testid="step-add"
        onClick={addStep}
        className="w-full border border-dashed border-zinc-300 rounded-md py-2 text-xs text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 inline-flex items-center justify-center gap-1"
      >
        <Plus className="w-3.5 h-3.5" />
        add step
      </button>

      {value.length > 1 && (
        <div className="text-[10px] text-zinc-400 mt-1">
          Drag the <GripVertical className="inline w-3 h-3 -mt-0.5" /> handle
          to reorder steps.
        </div>
      )}
    </div>
  );
}
